// @flow

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ButtonClickedEvent, LinkClickedEvent } from '@atlassian/mpac-analytics';
import {
  AppListingCTA,
  CTAKind,
  HostingSelectorModal,
  PurchaseIntent,
  valueForPurchaseIntent,
} from '@atlassian/mpac-app-listing';
import { CompatibleProductId } from '@atlassian/mpac-types';
import { AtlassianProductHostingTypeValues } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { hooks, MarketplaceAppUtils, UrlUtils } from '@atlassian/mpac-utils';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import React, { useState } from 'react';

import {
  safeToForwardToPartner,
  trackBuyButton,
  trackBuyModal,
  trackDownloadLink,
  trackGetItNow,
  trackGetItNowModal,
  trackTryButton,
  trackTryModal,
} from '../../../../../../../../common/utils/analytics/analytics';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_APP_HEADER,
  ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
  ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
  ANALYTICS_EVENT_SOURCE_HOSTING_MODEL,
  ANALYTICS_SUBJECT_ID_BUY_MODAL_TRIGGER,
  ANALYTICS_SUBJECT_ID_CTA,
  ANALYTICS_SUBJECT_ID_GET_MODAL_TRIGGER,
  ANALYTICS_SUBJECT_ID_REQUEST_APP_ADMIN,
  ANALYTICS_SUBJECT_ID_TRY_MODAL_TRIGGER,
} from '../../../../../../../analytics/Events';
import { toAnalyticsHosting, toAnalyticsHostingForHostingSelector } from '../utils';
import {
  getEmceeAppListingInstallLink,
  getEmceeAppListingRequestAppLink,
  getMailToLink,
  onlyJiraConfluenceProducts,
  useDisambiguatedSiteAndProduct,
} from './HeaderInjection.utils';
import SiteSelectorDialogWrapper from './SiteSelectorDialogWrapper/SiteSelectorDialogWrapper';

import type {
  CompatibleProduct,
  HostingSelectorAppVersion,
  PurchaseIntentType,
} from '@atlassian/mpac-app-listing';
import type {
  AppDetailsLink,
  ArtifactDownloadLink,
  LicensePurchaseLink,
  ManagePrivateListingLink,
  TrialLicenseActivationLink,
} from '@atlassian/mpac-types';
import type {
  AtlassianProductHostingType,
  MarketplaceAppPaymentModel,
} from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';

type AllVersions = {
  cloudAppVersion: ?HostingSelectorAppVersion,
  serverAppVersion: ?HostingSelectorAppVersion,
  dataCenterAppVersion: ?HostingSelectorAppVersion,
};

export type HeaderInjectionProps = {|
  appKey: string,
  appId: string,
  appName: string,
  paymentModel: MarketplaceAppPaymentModel,
  isConnectApp?: boolean,
  learnMoreLink?: ?string,
  compatibleProducts: $ReadOnlyArray<CompatibleProduct>,
  productHostingOptions: $ReadOnlyArray<AtlassianProductHostingType>,
  selectedHosting: AtlassianProductHostingType,
  versionInfo: AllVersions,
  artifactDownloadLink: ArtifactDownloadLink,
  trialLicenseActivationLink: TrialLicenseActivationLink,
  licensePurchaseLink: LicensePurchaseLink,
  privateListingsLink: ManagePrivateListingLink,
  privateRemotableOnly: boolean,
  appDetailsLink: AppDetailsLink,
  userLoggedIn: boolean,
|};

const { useSwallowQueryParam } = hooks;

export const SITE_SELECTOR_VISIBLE_QUERY_PARAM = 'site-selector-visible';

export const getSelectedVersion = (
  selectedHosting: AtlassianProductHostingType,
  versionInfo: AllVersions
): ?HostingSelectorAppVersion => {
  switch (selectedHosting) {
    case AtlassianProductHostingTypeValues.Cloud:
      return versionInfo.cloudAppVersion;
    case AtlassianProductHostingTypeValues.Server:
      return versionInfo.serverAppVersion;
    case AtlassianProductHostingTypeValues.DataCenter:
      return versionInfo.dataCenterAppVersion;
    default:
      throw new Error('Unexpected hosting type: ' + selectedHosting);
  }
};

const hasSiteSelectorCompatibleProduct = (appVersion: ?HostingSelectorAppVersion) => {
  const isRemoteable = appVersion && MarketplaceAppUtils.isRemoteable(appVersion.deployment);
  const isBitbucketCompatible = appVersion?.deployment.compatibleProducts.some(
    product => product.id === CompatibleProductId.Bitbucket
  );

  return isRemoteable && !isBitbucketCompatible;
};

const trackCTAClickEvent = purchaseIntent =>
  valueForPurchaseIntent(purchaseIntent)({
    [PurchaseIntent.TRY]: trackTryButton,
    [PurchaseIntent.BUY]: trackBuyButton,
    [PurchaseIntent.GET]: _ => {}, // We haven't been sending events for this historically
  });

const trackHostingModalOpenEvent = purchaseIntent =>
  valueForPurchaseIntent(purchaseIntent)({
    [PurchaseIntent.TRY]: trackTryModal,
    [PurchaseIntent.BUY]: trackBuyModal,
    [PurchaseIntent.GET]: trackGetItNowModal,
  });

const trackHostingModalCTAClickEvent = purchaseIntent =>
  valueForPurchaseIntent(purchaseIntent)({
    [PurchaseIntent.TRY]: trackTryButton,
    [PurchaseIntent.BUY]: trackBuyButton,
    [PurchaseIntent.GET]: trackGetItNow,
  });

const hostingTriggerAnalyticsSubjectId = purchaseIntent =>
  valueForPurchaseIntent(purchaseIntent)({
    [PurchaseIntent.TRY]: ANALYTICS_SUBJECT_ID_TRY_MODAL_TRIGGER,
    [PurchaseIntent.BUY]: ANALYTICS_SUBJECT_ID_BUY_MODAL_TRIGGER,
    [PurchaseIntent.GET]: ANALYTICS_SUBJECT_ID_GET_MODAL_TRIGGER,
  });

// eslint-disable-next-line max-lines-per-function -- glue between large components, hard to split
const HeaderInjection = (props: HeaderInjectionProps) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const siteSelectorQueryValue = useSwallowQueryParam(SITE_SELECTOR_VISIBLE_QUERY_PARAM);
  const [hostingSelectorPurchaseIntent, setHostingSelectorPurchaseIntent] = useState(
    PurchaseIntent.TRY
  );
  const [activeCTAKind, setActiveCTAKind] = useState(CTAKind.PRIMARY);
  const [hostingSelectorOpen, setHostingSelectorOpen] = useState(false);
  const [siteSelectorOpen, setSiteSelectorOpen] = useState(!!siteSelectorQueryValue);

  const jiraConfluenceCloudAppCompatibilities = props.versionInfo.cloudAppVersion
    ? onlyJiraConfluenceProducts(props.compatibleProducts).map(product => product.id)
    : [];

  const { furtherDisambiguationNeeded, disambiguationResult } = useDisambiguatedSiteAndProduct(
    props.userLoggedIn,
    jiraConfluenceCloudAppCompatibilities,
    props.paymentModel
  );

  const isUniversalApp = props.productHostingOptions.length > 1;
  const selectedVersion = getSelectedVersion(props.selectedHosting, props.versionInfo);

  const installAppHrefResolver = selectedSiteProduct =>
    getEmceeAppListingInstallLink(props.appKey, selectedSiteProduct);

  const requestAppHrefResolver = selectedSiteProduct =>
    getEmceeAppListingRequestAppLink(props.appKey, selectedSiteProduct);

  const loginRedirectPath = UrlUtils.updateQueryParam(
    window.location.href,
    SITE_SELECTOR_VISIBLE_QUERY_PARAM,
    'true'
  );

  const shareLink = getMailToLink(props.appName, props.appKey, props.appDetailsLink);

  const analyticsHosting = toAnalyticsHosting(props.selectedHosting);

  const handleRequestAppClick = () => {
    createAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_REQUEST_APP_ADMIN,
      source: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
        label: 'Request this app',
        hostingSelected: 'cloud',
        category: ANALYTICS_CATEGORY_APP_LISTING,
      },
    }).fire();
    if (hostingSelectorOpen) {
      setHostingSelectorOpen(false);
    }
    setSiteSelectorOpen(true);
  };

  const handleSiteSelectorClose = () => {
    setSiteSelectorOpen(false);
  };

  const handleHostingSelectorModalClose = () => {
    setHostingSelectorOpen(false);
  };

  const handleCloudAppDisambiguation = () => {
    if (furtherDisambiguationNeeded) {
      setSiteSelectorOpen(true);
    } else if (disambiguationResult) {
      window.location.href = installAppHrefResolver(disambiguationResult);
    }
  };

  const trackPossibleArtifactDownloadClick = (
    e: SyntheticMouseEvent<HTMLElement>,
    appVersion: ?HostingSelectorAppVersion
  ) => {
    const { currentTarget } = e;
    if (!(currentTarget instanceof window.Element) || !appVersion) return;
    const href = currentTarget.getAttribute('href');
    if (!href) return;

    const artifactDownloadUrl = props.artifactDownloadLink.expand({
      appId: props.appId,
      buildNumber: appVersion.buildNumber,
    });

    if (href === artifactDownloadUrl) {
      trackDownloadLink(artifactDownloadUrl);
    }
  };

  const openHostingSelectModal = (purchaseIntent: PurchaseIntentType) => {
    setHostingSelectorOpen(true);
    createAnalyticsEvent({
      eventType: ButtonClickedEvent,
      eventCategory: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      actionSubjectId: hostingTriggerAnalyticsSubjectId(purchaseIntent),
      source: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        source: ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
        hosting: camelCase(props.selectedHosting),
      },
    }).fire();

    safeToForwardToPartner(props.appId, () => {
      trackHostingModalOpenEvent(purchaseIntent)(analyticsHosting);
    });
  };

  const handleCTAClick = (e, purchaseIntent, ctaKind) => {
    const { target } = e || {};
    if (target instanceof window.Element) {
      createAnalyticsEvent({
        eventType: ButtonClickedEvent,
        eventCategory: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        actionSubjectId: ANALYTICS_SUBJECT_ID_CTA,
        source: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        attributes: {
          // eslint-disable-next-line unicorn/prefer-dom-node-text-content
          label: target.innerText,
          eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
          source: ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
          hosting: camelCase(props.selectedHosting),
        },
      }).fire();
    }

    const cloudSupported = props.productHostingOptions.includes(
      AtlassianProductHostingTypeValues.Cloud
    );

    if (isUniversalApp) {
      e.preventDefault();
      setHostingSelectorPurchaseIntent(purchaseIntent);
      setActiveCTAKind(ctaKind);
      openHostingSelectModal(purchaseIntent);
    } else {
      safeToForwardToPartner(props.appId, () => {
        trackCTAClickEvent(purchaseIntent)(analyticsHosting);
        trackPossibleArtifactDownloadClick(e, selectedVersion);
      });
    }

    if (!isUniversalApp && cloudSupported && hasSiteSelectorCompatibleProduct(selectedVersion)) {
      handleCloudAppDisambiguation();
    }
  };

  const handleHostingSelectorCTAClick = (e, hosting, appVersion) => {
    const { target } = e || {};
    if (target instanceof window.Element) {
      createAnalyticsEvent({
        eventType: ButtonClickedEvent,
        eventCategory: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        actionSubjectId: ANALYTICS_SUBJECT_ID_CTA,
        attributes: {
          // eslint-disable-next-line unicorn/prefer-dom-node-text-content
          label: startCase(toLower(target.innerText)),
          eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
          source: ANALYTICS_EVENT_SOURCE_HOSTING_MODEL,
          hosting: toAnalyticsHostingForHostingSelector(hosting),
        },
        source: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      }).fire();
    }

    safeToForwardToPartner(props.appId, () => {
      trackHostingModalCTAClickEvent(hostingSelectorPurchaseIntent)(toAnalyticsHosting(hosting));
      trackPossibleArtifactDownloadClick(e, appVersion);
    });

    const cloudSupported = hosting === AtlassianProductHostingTypeValues.Cloud;

    if (cloudSupported && hasSiteSelectorCompatibleProduct(appVersion)) {
      handleCloudAppDisambiguation();
      setHostingSelectorOpen(false);
    }
  };

  return (
    <>
      <SiteSelectorDialogWrapper
        installAppHrefResolver={installAppHrefResolver}
        requestAppHrefResolver={requestAppHrefResolver}
        appCompatibilities={jiraConfluenceCloudAppCompatibilities}
        appKey={props.appKey}
        appName={props.appName}
        paymentModel={props.paymentModel}
        appDetailsLink={props.appDetailsLink}
        onClose={handleSiteSelectorClose}
        isOpen={siteSelectorOpen}
        loginRedirectPath={loginRedirectPath}
      />
      <HostingSelectorModal
        appKey={props.appKey}
        appId={props.appId}
        privateRemotableOnly={props.privateRemotableOnly}
        onCTAClick={handleHostingSelectorCTAClick}
        artifactDownloadLink={props.artifactDownloadLink}
        trialLicenseActivationLink={props.trialLicenseActivationLink}
        licensePurchaseLink={props.licensePurchaseLink}
        privateListingsLink={props.privateListingsLink}
        purchaseIntent={hostingSelectorPurchaseIntent}
        activeCTAKind={activeCTAKind}
        highlightedHosting={props.selectedHosting}
        serverAppVersion={props.versionInfo.serverAppVersion}
        cloudAppVersion={props.versionInfo.cloudAppVersion}
        dataCenterAppVersion={props.versionInfo.dataCenterAppVersion}
        shareWithAdminLink={shareLink}
        isOpen={hostingSelectorOpen}
        onClose={handleHostingSelectorModalClose}
        onRequestAppClick={handleRequestAppClick}
      />

      {selectedVersion && (
        <AppListingCTA
          appKey={props.appKey}
          appId={props.appId}
          appVersion={selectedVersion}
          privateRemotableOnly={props.privateRemotableOnly}
          selectedHosting={props.selectedHosting}
          artifactDownloadLink={props.artifactDownloadLink}
          trialLicenseActivationLink={props.trialLicenseActivationLink}
          licensePurchaseLink={props.licensePurchaseLink}
          onClick={handleCTAClick}
          privateListingsLink={props.privateListingsLink}
        />
      )}
    </>
  );
};

export default HeaderInjection;
